'use client';
import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _extends } from "@swc/helpers/_/_extends";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@kamio/ui/components/form';
import { Input } from '@kamio/ui/components/input';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Captcha } from './captcha';
import { LoadingButton } from '../loading-button';
// Duplication due to nextjs issue, cannot export objects from server functions...
const signUpSchema = z.object({
    email: z.string({
        message: 'Required'
    }).email({
        message: 'Invalid email address'
    }),
    password: z.string({
        message: 'Required'
    }).min(8, {
        message: 'Password must be at least 8 characters'
    }),
    confirmPassword: z.string({
        message: 'Required'
    }),
    skipCaptcha: z.boolean().optional(),
    captcha: z.string({
        message: 'Required'
    }).optional()
}).refine((data)=>data.password === data.confirmPassword, {
    message: 'Passwords don\'t match',
    path: [
        'confirmPassword'
    ]
}).refine((data)=>data.skipCaptcha || data.captcha, {
    message: 'Captcha is required.',
    path: [
        'captcha'
    ]
});
export function SignUpForm({ signUp, captchaSiteKey }) {
    'use client';
    const form = useForm({
        resolver: zodResolver(signUpSchema),
        mode: 'onChange',
        defaultValues: {
            skipCaptcha: !captchaSiteKey
        }
    });
    function onSubmit(data) {
        return _async_to_generator(function*() {
            try {
                const result = yield signUp({
                    email: data.email,
                    password: data.password,
                    confirmPassword: data.confirmPassword
                });
                if (result.error) {
                    throw new Error(result.error);
                }
                toast.success('Signed Up Sucessfully. An email has been sent to confirm your email address.', {
                    dismissible: true,
                    duration: Infinity
                });
            } catch (ex) {
                if (ex instanceof Error) {
                    toast.error(`An Error Occured: ${ex.message}`);
                }
            }
        })();
    }
    return /*#__PURE__*/ _jsx(Form, _extends({}, form, {
        children: /*#__PURE__*/ _jsx("form", {
            onSubmit: form.handleSubmit(onSubmit),
            className: "animate-in",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(FormField, {
                        control: form.control,
                        name: "email",
                        render: ({ field })=>/*#__PURE__*/ _jsxs(FormItem, {
                                children: [
                                    /*#__PURE__*/ _jsx(FormLabel, {
                                        children: "Email"
                                    }),
                                    /*#__PURE__*/ _jsx(FormControl, {
                                        children: /*#__PURE__*/ _jsx(Input, _extends({}, field, {
                                            autoComplete: "email username"
                                        }))
                                    }),
                                    /*#__PURE__*/ _jsx(FormMessage, {})
                                ]
                            })
                    }),
                    /*#__PURE__*/ _jsx(FormField, {
                        control: form.control,
                        name: "password",
                        render: ({ field })=>/*#__PURE__*/ _jsxs(FormItem, {
                                children: [
                                    /*#__PURE__*/ _jsx(FormLabel, {
                                        children: "Password"
                                    }),
                                    /*#__PURE__*/ _jsx(FormControl, {
                                        children: /*#__PURE__*/ _jsx(Input, _extends({}, field, {
                                            type: "password",
                                            autoComplete: "new-password"
                                        }))
                                    }),
                                    /*#__PURE__*/ _jsx(FormMessage, {})
                                ]
                            })
                    }),
                    /*#__PURE__*/ _jsx(FormField, {
                        control: form.control,
                        name: "confirmPassword",
                        render: ({ field })=>/*#__PURE__*/ _jsxs(FormItem, {
                                children: [
                                    /*#__PURE__*/ _jsx(FormLabel, {
                                        children: "Confirm Password"
                                    }),
                                    /*#__PURE__*/ _jsx(FormControl, {
                                        children: /*#__PURE__*/ _jsx(Input, _extends({}, field, {
                                            type: "password",
                                            autoComplete: "new-password"
                                        }))
                                    }),
                                    /*#__PURE__*/ _jsx(FormMessage, {})
                                ]
                            })
                    }),
                    captchaSiteKey && /*#__PURE__*/ _jsx(FormField, {
                        control: form.control,
                        name: "captcha",
                        render: ({ field })=>/*#__PURE__*/ _jsxs(FormItem, {
                                children: [
                                    /*#__PURE__*/ _jsx(FormControl, {
                                        children: /*#__PURE__*/ _jsx("div", {
                                            style: {
                                                display: 'flex',
                                                justifyContent: 'space-around'
                                            },
                                            children: /*#__PURE__*/ _jsx(Captcha, _extends({
                                                siteKey: captchaSiteKey
                                            }, field))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(FormMessage, {})
                                ]
                            })
                    }),
                    /*#__PURE__*/ _jsx(LoadingButton, {
                        className: "w-full",
                        type: "submit",
                        isLoading: form.formState.isSubmitting,
                        children: "Sign Up"
                    })
                ]
            })
        })
    }));
}
